import {useEffect, useMemo, useRef, useState} from 'react';
import {ehrService} from '../../services/ehrService';

const hasValue = (value: any) => value != null && String(value).trim() !== '';

const ehrPatientSourceId = (patient: any) =>
  patient?.ehrSourceId || patient?.id || '';

// Demographic fields a thin live-search hit can be missing but that the
// cached record (and the UI row / selection payload) cares about.
const HYDRATABLE_FIELDS = [
  'dob',
  'mrn',
  'accountNo',
  'language',
  'gender',
  'phone',
  'email',
  'middleName',
  'pcp',
  'ehrType',
];

// A live EHR (Ayden) search hit often arrives with an identity and a name but
// no demographics — it renders as a bare name because the row's second line is
// built from dob / mrn / language. Treat such a record as "thin".
export const isThinEhrPatient = (patient: any) =>
  Boolean(
    patient &&
    ehrPatientSourceId(patient) &&
    !hasValue(patient.dob) &&
    !hasValue(patient.mrn) &&
    !hasValue(patient.language),
  );

export const thinEhrPatientSourceIds = (patients: any[] = []) => {
  const ids = new Set<string>();
  patients.forEach(patient => {
    if (isThinEhrPatient(patient)) ids.add(ehrPatientSourceId(patient));
  });
  return [...ids];
};

// Fill missing demographics on each patient from its cached record, keyed by
// EHR source id. Live identity/name fields always win; cached values only fill
// gaps, so we never clobber what the search hit already provided.
export const applyCachedEhrDemographics = (
  patients: any[] = [],
  cacheById: Record<string, any> = {},
) => {
  if (!patients.length || !Object.keys(cacheById).length) return patients;
  return patients.map(patient => {
    const cached = cacheById[ehrPatientSourceId(patient)];
    if (!cached) return patient;
    const merged = {...cached, ...patient};
    HYDRATABLE_FIELDS.forEach(field => {
      if (!hasValue(merged[field]) && hasValue(cached[field])) {
        merged[field] = cached[field];
      }
    });
    return merged;
  });
};

// Backfills thin EHR search hits (live-search results carrying no demographics)
// from the EHR cache by source id, so they render the same language / DOB / MRN
// line as cached or encounter-backed patients instead of a bare name.
export default function useHydratedEhrPatients(
  ehrPatients: any[] = [],
  companyId: any,
  enabled = true,
) {
  const [cacheById, setCacheById] = useState<Record<string, any>>({});
  // Source ids we have already asked the cache about, so a thin hit that the
  // cache can't resolve isn't refetched on every render.
  const requestedIdsRef = useRef<Set<string>>(new Set());

  const pendingIds = useMemo(() => {
    if (!enabled || !companyId) return [];
    return thinEhrPatientSourceIds(ehrPatients).filter(
      id => !requestedIdsRef.current.has(id),
    );
  }, [companyId, enabled, ehrPatients]);

  const pendingKey = pendingIds.join('|');

  useEffect(() => {
    if (!pendingKey) return undefined;
    const ids = pendingKey.split('|');
    let cancelled = false;
    // Cache first, then a capped live per-patient detail fetch for brand-new
    // EHR patients that aren't cached yet — one standardized resolve, not a
    // cache-only lookup that silently no-ops on uncached hits.
    ehrService
      .resolveEhrPatients(companyId, ids)
      .then((map: Record<string, any>) => {
        if (cancelled || !map) return;
        // Only retire ids we actually resolved. A transient cache/live miss
        // (uncached patient, in-flight cancel, network blip) leaves the id
        // eligible for a later attempt instead of parking it blank for the
        // life of the search — the old code marked every id "requested"
        // up-front, so a single miss meant the row never populated again.
        Object.keys(map).forEach(id => requestedIdsRef.current.add(id));
        if (!Object.keys(map).length) return;
        setCacheById(previous => ({...previous, ...map}));
      })
      .catch(() => {
        /* hydration is best-effort; leave ids eligible to retry */
      });
    return () => {
      cancelled = true;
    };
  }, [companyId, pendingKey]);

  return useMemo(
    () => applyCachedEhrDemographics(ehrPatients, cacheById),
    [ehrPatients, cacheById],
  );
}
